/**
 * Shared error handling hooks for consistent error management across the application
 */

import { useCallback } from 'react';
import { useAlert } from '../context/AlertContext';

/**
 * Custom hook for handling API errors consistently
 * @returns {Object} Error handling utilities
 */
export const useApiErrorHandler = () => {
    const { setErrorAlert, setAlert, setInfoAlert } = useAlert();
    
    /**
     * Handle API errors with user-friendly messages
     * @param {Error|string} error - Error object or error message
     * @param {string} context - Context description for logging
     * @param {boolean} showAlert - Whether to show alert to user (default: true)
     * @returns {string} Processed error message
     */
    const handleError = useCallback((error, context = '', showAlert = true) => {
        let errorMessage;
        
        if (error instanceof Error) {
            errorMessage = error.message;
        } else if (typeof error === 'string') {
            errorMessage = error;
        } else {
            errorMessage = 'An unexpected error occurred';
        }
        
        // Add context to the message if provided
        const contextualMessage = context 
            ? `Failed to ${context}: ${errorMessage}`
            : errorMessage;
        
        // Log error for debugging
        console.error(`Error ${context}:`, error);
        
        // Show user-friendly alert if requested
        if (showAlert) {
            setErrorAlert(contextualMessage);
        }
        
        return contextualMessage;
    }, [setErrorAlert]);

    /**
     * Handle API warnings (non-critical errors)
     * @param {string} message - Warning message
     * @param {string} context - Context description
     */
    const handleWarning = useCallback((message, context = '') => {
        const contextualMessage = context 
            ? `Warning ${context}: ${message}`
            : message;
            
        console.warn(contextualMessage);
        setAlert(contextualMessage);
    }, [setAlert]);

    /**
     * Handle success messages
     * @param {string} message - Success message
     * @param {string} context - Context description
     */
    const handleSuccess = useCallback((message, context = '') => {
        const contextualMessage = context 
            ? `${context}: ${message}`
            : message;
            
        setInfoAlert(contextualMessage);
    }, [setInfoAlert]);

    /**
     * Wrapper for async operations with automatic error handling
     * @param {Function} asyncOperation - Async function to execute
     * @param {string} context - Context description for errors
     * @param {Object} options - Additional options
     * @returns {Promise} Result of the async operation or null if error
     */
    const withErrorHandling = useCallback(async (asyncOperation, context = '', options = {}) => {
        const { 
            showAlert = true, 
            suppressErrors = false,
            onSuccess = null,
            onError = null 
        } = options;
        
        try {
            const result = await asyncOperation();
            
            if (onSuccess) {
                onSuccess(result);
            }
            
            return result;
        } catch (error) {
            const errorMessage = handleError(error, context, showAlert && !suppressErrors);
            
            if (onError) {
                onError(error, errorMessage);
            }
            
            if (!suppressErrors) {
                throw error;
            }
            
            return null;
        }
    }, [handleError]);

    return {
        handleError,
        handleWarning,
        handleSuccess,
        withErrorHandling
    };
};

/**
 * Hook for handling form validation errors
 * @returns {Object} Form error handling utilities
 */
export const useFormErrorHandler = () => {
    const { setErrorAlert } = useAlert();

    /**
     * Validate required fields
     * @param {Object} data - Form data
     * @param {string[]} requiredFields - Array of required field names
     * @returns {Object} Validation result with isValid and errors
     */
    const validateRequired = useCallback((data, requiredFields) => {
        const errors = {};
        const missingFields = [];

        requiredFields.forEach(field => {
            if (!data[field] || (typeof data[field] === 'string' && data[field].trim() === '')) {
                errors[field] = 'This field is required';
                missingFields.push(field);
            }
        });

        const isValid = missingFields.length === 0;
        
        if (!isValid) {
            setErrorAlert(`Please fill in all required fields: ${missingFields.join(', ')}`);
        }

        return { isValid, errors, missingFields };
    }, [setErrorAlert]);

    /**
     * Validate field lengths
     * @param {Object} data - Form data
     * @param {Object} fieldLimits - Object with field names as keys and max lengths as values
     * @returns {Object} Validation result with isValid and errors
     */
    const validateLengths = useCallback((data, fieldLimits) => {
        const errors = {};
        const invalidFields = [];

        Object.entries(fieldLimits).forEach(([field, maxLength]) => {
            if (data[field] && data[field].length > maxLength) {
                errors[field] = `Maximum ${maxLength} characters allowed`;
                invalidFields.push(`${field} (max ${maxLength})`);
            }
        });

        const isValid = invalidFields.length === 0;
        
        if (!isValid) {
            setErrorAlert(`Field length validation failed: ${invalidFields.join(', ')}`);
        }

        return { isValid, errors, invalidFields };
    }, [setErrorAlert]);

    /**
     * Comprehensive form validation
     * @param {Object} data - Form data
     * @param {Object} rules - Validation rules
     * @returns {Object} Complete validation result
     */
    const validateForm = useCallback((data, rules = {}) => {
        const { required = [], lengths = {}, custom = [] } = rules;
        
        let allErrors = {};
        let isValid = true;

        // Required field validation
        if (required.length > 0) {
            const requiredResult = validateRequired(data, required);
            if (!requiredResult.isValid) {
                isValid = false;
                allErrors = { ...allErrors, ...requiredResult.errors };
            }
        }

        // Length validation
        if (Object.keys(lengths).length > 0) {
            const lengthResult = validateLengths(data, lengths);
            if (!lengthResult.isValid) {
                isValid = false;
                allErrors = { ...allErrors, ...lengthResult.errors };
            }
        }

        // Custom validation
        custom.forEach(validator => {
            const result = validator(data);
            if (!result.isValid) {
                isValid = false;
                allErrors = { ...allErrors, ...result.errors };
            }
        });

        return { isValid, errors: allErrors };
    }, [validateRequired, validateLengths]);

    return {
        validateRequired,
        validateLengths,
        validateForm
    };
};

export default {
    useApiErrorHandler,
    useFormErrorHandler
};